/**
 * The phone app's native side (apps/mobile; docs/mobile/build-plan.md): the
 * app's own Tauri plugin "neosocial" and tauri-plugin-opener, reached through
 * `window.__TAURI__` (withGlobalTauri).
 *
 * - Push: the OS's push token (asking for the notification permission with
 *   the system's own prompt the first time) goes to the server, which sends
 *   DMs, mentions and calls to it; signing out removes it first.
 * - Voice: Android keeps a microphone foreground service with its "Connected
 *   to voice" notification while you are in a call, so voice carries on with
 *   the screen off (iOS needs nothing: the audio background mode).
 * - External links open in the system browser.
 *
 * Every call does nothing outside the mobile flavor and fails quietly
 * (console.warn): the app works without its native side.
 */
import { api } from './api';
import { isMobileApp } from './config';
import { isIOSWebKit } from './device';

interface TauriGlobal {
  core?: { invoke?<T>(cmd: string, args?: Record<string, unknown>): Promise<T> };
  event?: {
    listen?<T>(event: string, cb: (e: { payload: T }) => void): Promise<() => void>;
  };
}

function tauri(): TauriGlobal | undefined {
  return isMobileApp() ? (globalThis as { __TAURI__?: TauriGlobal }).__TAURI__ : undefined;
}

/** A native command's answer, or null when there is none (not the phone app, or it failed). */
async function call<T>(cmd: string, args?: Record<string, unknown>): Promise<T | null> {
  const invoke = tauri()?.core?.invoke;
  if (!invoke) return null;
  try {
    return await invoke<T>(cmd, args);
  } catch (e) {
    console.warn(`${cmd} failed:`, e);
    return null;
  }
}

/** Listens to an event the app emits; does nothing outside the phone app. */
export function listenNative<T>(event: string, cb: (payload: T) => void): void {
  const listen = tauri()?.event?.listen;
  if (!listen) return;
  listen<T>(event, (e) => cb(e.payload)).catch((e: unknown) =>
    console.warn(`listening to ${event} failed:`, e),
  );
}

export type MobilePlatform = 'ios' | 'android';

/** The phone's system, for the session list and the gateway (the app ships for these two). */
export function mobilePlatform(): MobilePlatform {
  return isIOSWebKit() ? 'ios' : 'android';
}

interface AppInfo {
  platform: MobilePlatform;
  version: string;
  build: string;
}

let info: AppInfo | null = null;

/** Asks the app for its version once, at start (connectMobileShell). */
export async function loadAppInfo(): Promise<void> {
  info ??= await call<AppInfo>('plugin:neosocial|app_info');
}

/** The app's version (Settings > About, the session's app_version); the store version until it answers. */
export function mobileAppVersion(): string {
  return info?.version || '1.0.0';
}

/** The push token this session gave the server, removed again at sign-out. */
let pushToken: string | null = null;

/**
 * Asks the OS for the push token (the system's permission prompt the first
 * time) and saves it on the account. Once per session: after sign-in, and at
 * every start while signed in. A refusal leaves the app without push.
 */
export async function registerPush(): Promise<void> {
  const r = await call<{ platform: MobilePlatform; token: string }>(
    'plugin:neosocial|register_push',
  );
  if (!r?.token) return;
  try {
    await api.client.put<void>('/users/@me/push-devices', {
      platform: r.platform,
      token: r.token,
      app_version: mobileAppVersion(),
    });
    pushToken = r.token;
  } catch (e) {
    console.warn('the push token could not be saved:', e);
  }
}

/** Sign-out: this phone gets no more pushes for the account (while the session still works). */
export async function unregisterPush(): Promise<void> {
  const token = pushToken;
  pushToken = null;
  if (!token) return;
  try {
    await api.client.delete<void>(`/users/@me/push-devices/${encodeURIComponent(token)}`);
  } catch (e) {
    console.warn('the push token could not be removed:', e);
  }
}

/** In a call or not: Android's voice foreground service, named for the channel. */
export function setVoiceActive(active: boolean, title?: string): void {
  void call<null>('plugin:neosocial|set_voice_active', title ? { active, title } : { active });
}

/** The push the app was opened with (a tapped notification), once; null otherwise. */
export function takeLaunchPush(): Promise<unknown> {
  return call<unknown>('plugin:neosocial|take_launch_push');
}

/**
 * Opens a link in the system browser (a mailto: link in the mail app).
 * False when the app couldn't: not the phone app, or a link its opener
 * doesn't allow.
 */
export async function openExternal(url: string): Promise<boolean> {
  const invoke = tauri()?.core?.invoke;
  if (!invoke) return false;
  try {
    await invoke<null>('plugin:opener|open_url', { url });
    return true;
  } catch (e) {
    console.warn('plugin:opener|open_url failed:', e);
    return false;
  }
}
